import React, { FC, SyntheticEvent, useEffect, useState } from 'react';
import filtersStyles from '../../Filters.module.scss';
import { CloseLight, Action } from 'esa-ui-library';
import { PropertyType, useStore } from '../../../../../common';
import { FilterDropdown } from '../FilterDropdown/FilterDropdown';
import { getActiveLabels, getFilterValue, getUpdateOptions, selectPreselectedOptions } from '../../Filters.helper';
import { cloneDeep, find, startCase } from 'lodash';
import useFilters from '../../../../../common/hooks/useFilters';

interface GeneralFilterProps {
	filterName: string;
	isMultiple?: boolean;
	dataTestId?: string;
}

const GeneralFilter: FC<GeneralFilterProps> = ({ filterName, isMultiple = true, dataTestId }): JSX.Element => {
	const [state] = useStore();
	const { filters } = state;
	const [filterOptions, setFiltersOptions] = useState<PropertyType[]>([]);
	const [opened, setOpened] = useState(false);
	const { onFilter } = useFilters();

	const onFilterChange = (option: PropertyType) => {
		const updateOptions = isMultiple
			? getUpdateOptions(option, filterOptions)
			: getUpdateOptions(option, state[filterName]);
		const filterValue = getFilterValue(updateOptions);
		setOpened(false);

		onFilter([{ name: filterName, value: filterValue }]);
	};

	const onFilterRemove = (e: SyntheticEvent) => {
		e.stopPropagation();

		onFilter([{ name: filterName, value: '' }]);
	};

	const getLabelContent = () => {
		const activeLabels = getActiveLabels(filterOptions);
		if (!activeLabels.length) return 'All';

		return (
			<>
				{activeLabels[0]}
				{activeLabels.length > 1 ? `+${activeLabels.length - 1}` : ''}
				<i className={filtersStyles.ClearFilter} onClick={onFilterRemove}>
					<CloseLight size={16} />
				</i>
			</>
		);
	};

	useEffect(() => {
		if (state[filterName]) {
			setFiltersOptions(cloneDeep(state[filterName]));
		}
	}, [state[filterName]]);

	useEffect(() => {
		const filter = find(filters, ['name', filterName])?.value || '';
		setFiltersOptions(selectPreselectedOptions(state[filterName], filter));
	}, [filters]);

	return (
		<Action
			label={startCase(filterName)}
			labelContent={getLabelContent()}
			controlled
			select
			onDropdownChange={setOpened}
			opened={opened}
			className={`${filtersStyles.Filter} 
			${getActiveLabels(filterOptions).length ? filtersStyles.Active : ''} `}
			dataId={dataTestId || 'general-filter'}
		>
			<FilterDropdown data={filterOptions} onChange={onFilterChange} isSearchable />
		</Action>
	);
};

export { GeneralFilter };
